import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { usePlayerAuth } from "../context/PlayerAuthContext";
import { ShareConsentCheckbox } from "../components/ShareConsentCheckbox";
import { ACADEMY_LOGO } from "../data/assets";
import { Lock, Loader2, XCircle } from "lucide-react";

export default function Claim() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { setupInfo, register } = usePlayerAuth();
  const [state, setState] = useState("checking"); // checking | ok | invalid
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    setupInfo(token).then((d) => {
      if (d.valid) { setState("ok"); setEmail(d.email); setName(d.name || ""); setConsent(!!d.share_consent); }
      else setState("invalid");
    }).catch(() => setState("invalid"));
  }, [token, setupInfo]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    setBusy(true);
    try {
      await register(token, password, name.trim(), consent);
      toast.success("Profile created! Welcome to The Next O Technical Academy.");
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "This setup link is invalid or already used.");
      if (err?.response?.status === 409) setState("invalid");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#111] text-white flex items-center justify-center px-6 grain">
      <div className="absolute inset-0 red-glow pointer-events-none" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative z-10 max-w-md w-full bg-[#141414] border border-white/10 rounded-xl p-8">
        <img src={ACADEMY_LOGO} alt="" className="w-40 h-auto object-contain mb-5" />
        {state === "checking" && <div className="text-center py-6"><Loader2 size={36} className="text-[#C41E24] mx-auto animate-spin" /></div>}
        {state === "invalid" && (
          <div className="text-center">
            <XCircle size={40} className="text-[#C41E24] mx-auto mb-4" />
            <h1 className="font-headings text-2xl uppercase mb-2">Link Not Valid</h1>
            <p className="text-white/60 text-sm mb-5">This setup link is invalid, expired, or already used. If you've set up your account, just log in.</p>
            <button onClick={() => navigate("/")} className="bg-[#C41E24] px-6 py-3 rounded-sm font-bold uppercase text-sm">Back Home</button>
          </div>
        )}
        {state === "ok" && (
          <>
            <div className="flex items-center gap-2 mb-1"><Lock size={16} className="text-[#C41E24]" /><span className="text-[10px] font-bold uppercase tracking-widest text-[#C41E24]">Secure Setup</span></div>
            <h1 className="font-headings text-3xl uppercase tracking-wide mb-1">Create Your Password</h1>
            <p className="text-white/55 text-sm mb-5">for <b className="text-white">{email}</b></p>
            <form onSubmit={submit} className="space-y-3">
              <input data-testid="claim-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
                className="w-full bg-[#0d0d0d] border border-white/10 rounded-md p-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]" />
              <input data-testid="claim-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Choose a password (min 6 chars)"
                className="w-full bg-[#0d0d0d] border border-white/10 rounded-md p-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]" />
              <ShareConsentCheckbox testid="claim-share-consent" checked={consent} onChange={setConsent} />
              <button data-testid="claim-submit" type="submit" disabled={busy}
                className="w-full bg-[#C41E24] hover:bg-[#A0161D] text-white font-subheadings font-bold uppercase tracking-wider py-3 rounded-sm transition-colors disabled:opacity-60">
                {busy ? "Creating…" : "Create My Profile"}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
